import { DataGrid, theme, Button, PopUp, TableCell } from '@ketch-com/deck'
import { Typography } from '@mui/material'
import { useMemo, useRef } from 'react'
import { useTableFilters } from '../hooks/useTableFilters'
import { useDataSentryContext } from 'pages/dataSentry/context/DataSentryContext'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { SearchTextInput } from 'components/searchTextInput/SearchTextInput'
import { useDataSentryScanUrls } from 'api/dataSentry/queries/useDataSentryScanUrls'
import { ScanUrlList } from '@ketch-com/windlass/dist/dataSentry/v1/scans/scan'

interface ScanUrlsModalProps {
  isOpen: boolean
  onClose: () => void
}

export const ScanUrlsModal: React.FC<ScanUrlsModalProps> = ({ isOpen, onClose }) => {
  const { searchQuery, debouncedSearchQuery, setSearchQuery, paginationModel, setPaginationModel } = useTableFilters(10)

  const { selectedProperty } = useDataSentryContext()

  const { data: scannedUrls, isLoading: isLoadingScannedUrls } = useDataSentryScanUrls({
    params: {
      'property-codes': selectedProperty?.code,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      query: debouncedSearchQuery,
    },
  })

  const rowCountRef = useRef(scannedUrls?.total || 0)
  const rowCount = useMemo(() => {
    if (scannedUrls?.total !== undefined) {
      rowCountRef.current = scannedUrls?.total
    }
    return rowCountRef.current
  }, [scannedUrls?.total])

  const columns: GridColDef<ScanUrlList>[] = [
    {
      align: 'left',
      field: 'pageUrl',
      headerName: 'URL',
      flex: 2,
      headerAlign: 'left',
      sortable: false,
      type: 'string',
      renderHeader: params => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ value }: GridRenderCellParams<ScanUrlList>) => {
        return (
          <TableCell>
            <Typography variant="label">{value}</Typography>
          </TableCell>
        )
      },
    },
    {
      align: 'left',
      field: 'propertyCode',
      headerName: 'Property',
      flex: 1,
      headerAlign: 'left',
      sortable: false,
      type: 'string',
      renderHeader: params => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ value }: GridRenderCellParams<ScanUrlList>) => {
        return (
          <TableCell>
            <Typography variant="label">{value}</Typography>
          </TableCell>
        )
      },
    },
  ]

  return (
    <PopUp
      isLoading={isLoadingScannedUrls}
      isOpen={isOpen}
      variant="modal"
      onClose={onClose}
      title="URLs Scanned"
      popUpWidth="940px"
      popUpActionChildren={
        <Button variant="outlined" color="primary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <SearchTextInput
        onChange={nextValue => setSearchQuery(nextValue)}
        value={searchQuery}
        showClearButton
        placeholder="Search"
      />
      <DataGrid
        sx={{
          border: 0,
          '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox):hover': {
            backgroundImage: 'unset',
            cursor: 'auto',
          },
        }}
        autosizeOnMount
        autosizeOptions={{
          expand: true,
          includeHeaders: true,
          includeOutliers: false,
        }}
        getRowHeight={() => 'auto'}
        columns={columns}
        getRowId={row => row.pageUrl + row.propertyCode}
        rows={scannedUrls?.scanUrls || []}
        checkboxSelection={false}
        hideFooter={false}
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnPinning
        disableColumnReorder
        disableColumnResize
        disableChildrenSorting={false}
        disableRowHoverStates
        loading={isLoadingScannedUrls}
        pagination
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5, 10, 20]}
        noRowsOverlayProps={{ buttonTitle: '' }}
      />
    </PopUp>
  )
}
